import { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useSearchParams } from 'react-router-dom';

import { PlaybackController } from './PlaybackController';
import { PlaybackControls } from './PlaybackControls';
import { TranscriptBlocks } from './TranscriptBlocks';
import { TranscriptionHeader } from './TranscriptHeader';
import { TranscriptInconsistencies } from './TranscriptInconsistencies';
import { RecordingLoadingSpinner } from './TranscriptLoading';
import { TranscriptSummary } from './TranscriptSummary';
import { ViewModeToggle } from './ViewModeToggle';
import { LogicalSpeaker } from '../hooks/useTranscription';
import { LiveTranscriptBlock } from '../types/transcriptTypes';
import { TranscriptInconsistency, TranscriptSpeaker } from '@/api/types/backend-dto-types';

interface PlaybackTranscriptionProps {
  transcriptMetadata: {
    otherSpeakers: string[] | null;
    deponent: string | null;
    createdDate: Date | null;
    transcriptName: string | null;
    phraseList: string[] | null;
    objective: string | null;
    summary: string | null;
    speakers: TranscriptSpeaker[];
  };
  // Local state
  displayBlocks: LiveTranscriptBlock[];
  displayLogicalSpeakers: LogicalSpeaker[];
  displayInconsistencies: TranscriptInconsistency[];

  playbackController: PlaybackController;
  transcriptContainerRef: React.RefObject<HTMLDivElement>;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  handleBlockClick: (block: LiveTranscriptBlock) => void;
  handleScrollToBlockFromInconsistency: (blockId: string) => void;
  updateBlock?: (blockId: string, content?: string, speakerId?: string | null) => void;
  acceptDiarizationCorrection?: (correctionId: string, blockId: string) => void;
  rejectDiarizationCorrection?: (correctionId: string, blockId: string) => void;
  isLoadingMode: boolean;
}

// Animation variants for view mode transitions
const viewModeVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const transition = {
  duration: 0.2,
  ease: 'easeInOut',
};

export const PlaybackTranscription = ({
  displayBlocks,
  displayLogicalSpeakers,
  displayInconsistencies,
  transcriptMetadata,
  playbackController,
  transcriptContainerRef,
  handleScroll,
  handleBlockClick,
  handleScrollToBlockFromInconsistency,
  updateBlock,
  acceptDiarizationCorrection,
  rejectDiarizationCorrection,
  isLoadingMode,
}: PlaybackTranscriptionProps) => {
  const [viewMode, setViewMode] = useState<'transcript' | 'summary'>('transcript');
  const [searchParams] = useSearchParams();
  const { transcriptId } = useParams();
  const caseId = searchParams.get('caseId');

  return (
    <div className="flex h-full flex-col">
      <TranscriptionHeader
        transcriptName={transcriptMetadata?.transcriptName || ''}
        transcriptCreatedDate={transcriptMetadata?.createdDate || null}
        transcriptDeponent={transcriptMetadata?.deponent || null}
        transcriptObjective={transcriptMetadata?.objective || null}
        transcriptOtherSpeakers={transcriptMetadata?.otherSpeakers || null}
        transcriptPhraseList={transcriptMetadata?.phraseList || null}
        audioControls={<PlaybackControls playbackController={playbackController} />}
      />

      <div className="flex flex-grow flex-col overflow-y-auto">
        {isLoadingMode ? (
          <RecordingLoadingSpinner />
        ) : (
          <>
            <div className="flex flex-1 overflow-hidden bg-white">
              <div className="relative flex w-3/5 max-w-3/5 flex-col overflow-hidden bg-gray-50">
                <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
                <div
                  ref={transcriptContainerRef}
                  className="relative flex-1 overflow-y-auto p-4"
                  onScroll={handleScroll}
                >
                  <AnimatePresence mode="wait">
                    {viewMode === 'summary' ? (
                      <motion.div
                        key="summary"
                        variants={viewModeVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={transition}
                        className="h-full"
                      >
                        <TranscriptSummary
                          summary={transcriptMetadata?.summary || null}
                          speakers={transcriptMetadata?.speakers || []}
                          transcriptId={transcriptId || ''}
                          caseId={caseId || ''}
                          onSubfactClick={(blockId) => {
                            // Switch to transcript view and scroll to the block
                            setViewMode('transcript');
                            setTimeout(() => {
                              handleScrollToBlockFromInconsistency(blockId);
                            }, 100);
                          }}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="transcript"
                        variants={viewModeVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={transition}
                        className="h-full"
                      >
                        <TranscriptBlocks
                          displayBlocks={displayBlocks}
                          caseId={caseId || ''}
                          transcriptId={transcriptId || ''}
                          speakers={displayLogicalSpeakers}
                          currentTime={playbackController?.state.currentTime || 0}
                          onBlockClick={handleBlockClick}
                          onUpdateBlock={updateBlock}
                          onAcceptCorrection={acceptDiarizationCorrection}
                          onRejectCorrection={rejectDiarizationCorrection}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <TranscriptInconsistencies
                caseId={caseId ?? ''}
                displayInconsistencies={displayInconsistencies}
                isPlaybackMode={true}
                handleScrollToBlockFromInconsistency={handleScrollToBlockFromInconsistency}
                numberOfBlocks={displayBlocks.length}
              />
            </div>

            {playbackController && (
              <audio ref={playbackController.audioRef} preload="metadata" crossOrigin="anonymous" />
            )}
          </>
        )}
      </div>
    </div>
  );
};
